import { useTranslation } from 'react-i18next'
import { CurecallDocumentViewer, FileTextIcon, LockIcon } from '@honestica/curecall-ui'
import { TSmsLog } from 'src/types/sms-log'
import { TPatient } from 'src/types/patient'
import { getFullName } from 'src/utils/string-formatter.util'
import TimezoneAwareTime from 'src/components/timezone-aware-time/timezone-aware-time.component'
import { EPermissionSpecialAuthorization } from 'src/pages/configuration/permissions/_components/permissions.type'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useSmsDocument } from './use-sms-document.hook'
import { PDF_WORKER_SRC } from 'src/utils/pdf-worker.util'
import { isNotFoundError } from 'src/utils/http-error.util'
import { MissingDocumentFileBadge } from 'src/components/patient/missing-document-file-badge.component'

const InboundSmsLogCard = ({
  smsLog,
  patient,
}: {
  smsLog: TSmsLog
  patient: Pick<TPatient, 'id' | 'firstname' | 'lastname'>
}) => {
  const { project, hasSpecialAuthorization } = useCurrentProject()
  const { t } = useTranslation()
  const isImage = smsLog.type === 'IMAGE'
  const isDocument = smsLog.type === 'DOCUMENT'
  const isUnsupportedFile = smsLog.type === 'INVALID_FILE'
  const isUnexpectedFile = smsLog.type === 'UNEXPECTED_FILE'
  const canViewSensitiveData = hasSpecialAuthorization(EPermissionSpecialAuthorization.CAN_VIEW_SENSITIVE_DATA)

  const { isLoading, isError, error, objectUrl, mimeType, refetch } = useSmsDocument(
    project.id,
    patient.id,
    smsLog.id,
    (isImage || isDocument) && canViewSensitiveData,
  )

  const pdfLabels = {
    pageLabel: (current: number, total: number) => t('components.pdf-viewer.page-label', { current, total }),
    previousPageLabel: t('components.pdf-viewer.previous-page'),
    nextPageLabel: t('components.pdf-viewer.next-page'),
    zoomInLabel: t('components.image-lightbox.zoom-in'),
    zoomOutLabel: t('components.image-lightbox.zoom-out'),
    downloadLabel: t('common.actions.download'),
    loadErrorLabel: t('components.pdf-viewer.load-error'),
    retryLabel: t('components.sms.image-retry'),
    loadingLabel: t('components.pdf-viewer.loading'),
    zoomTo100Label: t('components.image-lightbox.zoom-to-100'),
    resetScaleLabel: t('components.image-lightbox.reset-view'),
  }

  const lightboxLabels = {
    closeLabel: t('common.actions.close'),
    zoomInLabel: t('components.image-lightbox.zoom-in'),
    zoomOutLabel: t('components.image-lightbox.zoom-out'),
    zoomTo100Label: t('components.image-lightbox.zoom-to-100'),
    resetViewLabel: t('components.image-lightbox.reset-view'),
    imageViewerLabel: t('components.image-lightbox.image-viewer'),
    downloadLabel: t('common.actions.download'),
  }

  return (
    <div className="c-flex c-flex-col c-ml-80">
      <p className="c-mb-0 c-self-end c-text-base c-text-gray-700">{getFullName(patient)}</p>

      {/* IMAGE or DOCUMENT — no permission */}
      {(isImage || isDocument) && !canViewSensitiveData ? (
        <div className="c-flex c-items-center c-gap-2 c-self-end c-rounded-xl c-border c-border-dashed c-border-grey-300 c-bg-grey-50 c-px-3 c-py-2 c-text-sm c-text-grey-600">
          <LockIcon className="c-size-4 c-shrink-0" />
          <p className="c-mb-0">{t('components.sms.image-restricted-access')}</p>
        </div>
      ) : isImage || isDocument ? (
        <div className="c-self-end">
          {isError && isNotFoundError(error) ? (
            <MissingDocumentFileBadge />
          ) : (
            <CurecallDocumentViewer
              objectUrl={objectUrl ?? ''}
              mimeType={mimeType}
              pdfWorkerSrc={PDF_WORKER_SRC}
              isLoading={isLoading}
              isError={isError}
              onRetry={refetch}
              variant="chat"
              openLabel={t('components.patient-variable-document.visualize')}
              lightboxLabels={lightboxLabels}
              pdfLabels={pdfLabels}
            />
          )}
        </div>
      ) : isUnexpectedFile ? (
        <div className="c-flex c-items-center c-gap-2 c-self-end c-rounded-xl c-border c-border-dashed c-border-grey-300 c-bg-grey-50 c-px-3 c-py-2 c-text-sm c-text-grey-600">
          <FileTextIcon className="c-size-4 c-shrink-0" />
          <p className="c-mb-0">{t('components.sms.unexpected-file')}</p>
        </div>
      ) : isUnsupportedFile ? (
        <div className="c-flex c-items-center c-gap-2 c-self-end c-rounded-xl c-border c-border-dashed c-border-grey-300 c-bg-grey-50 c-px-3 c-py-2 c-text-sm c-text-grey-600">
          <FileTextIcon className="c-size-4 c-shrink-0" />
          <p className="c-mb-0">{t('components.sms.unsupported-file')}</p>
        </div>
      ) : (
        <div className="c-mb-0 c-text-base c-rounded-xl c-p-2 c-border c-border-grey-500 c-bg-blue-200 c-rounded-tr-none">
          <p className="c-mb-0 c-whitespace-pre-wrap">{smsLog.message}</p>
        </div>
      )}

      <p className="c-self-end c-px-2 c-mb-0 c-text-grey-500">
        <TimezoneAwareTime date={smsLog.sentAt} formatter="readableDateWithHours" />
      </p>
    </div>
  )
}

export { InboundSmsLogCard }
